import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import Select from "react-select";
import { DatePicker } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import {
  FaCalendarAlt,
  FaUser,
  FaVenusMars,
  FaUsers,
  FaBirthdayCake,
  FaChurch,
  FaMapMarkerAlt,
  FaCross,
} from "react-icons/fa";

const UpdateConfirmationChild = ({ confirmationRecord }) => {
  let navigate = useNavigate();
  const style = {
    control: (base) => ({
      ...base,
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #4299e1",
      },
    }),
  };

  const [{ errorLogApi, placeHolderText }] = useAtom(gbState);
  const [formData, setFormData] = useState({
    ...confirmationRecord,
  });

  const genderOptions = [
    { value: 1, label: "Male" },
    { value: 2, label: "Female" },
    { value: 3, label: "Other" },
  ];

  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  const dropDownChange = (selected, dropdown) => {
    setFormData({
      ...formData,
      [dropdown.name]: selected,
    });
  };

  function formatString(str) {
    const words = str.replace(/_/g, " ").split(" ");
    return words
      .map((word, index) =>
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
      )
      .join(" ");
  }

  const handleConfirmationRegister = () => {
    const fieldToValidate = [
      "confirmation_date",
      "place_of_confirmation",
      "confirmed_by",
      "sex",
      "parents_name",
      "date_of_baptism",
      "date_of_birth",
    ];

    const emptyFields = fieldToValidate
      .filter((f) => !formData[f])
      .map(formatString);

    if (emptyFields.length > 0) {
      return toast.error(`${emptyFields.join(", ")} cannot be empty`);
    }

    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/confirmation/update/${confirmationRecord?._id}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: formData,
    };

    axios(config)
      .then(function (response) {
        toast.success(response?.data?.message);
        navigate("/confirmation/list");
      })
      .catch(function (error) {
        toast.error(
          error.response?.data
            ? JSON.stringify(error.response.data)
            : "An error occurred"
        );
        errorLogApi(error, "UpdateConfirmation", "/confirmation/update");
      });
  };

  return (
    <div className="max-w-4xl p-6 mx-auto bg-white rounded-lg shadow-lg">
      <h2 className="mb-6 text-2xl font-semibold text-gray-800">
        Update Confirmation Record
      </h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Confirmation Date
            </label>
            <DatePicker
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              name="confirmation_date"
              onChange={(date, dateString) =>
                setFormData({ ...formData, confirmation_date: dateString })
              }
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
              format="MM-DD-YYYY"
              suffixIcon={<FaCalendarAlt className="text-gray-400" />}
              value={dayjs(formData.confirmation_date, "YYYY-MM-DD")}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Name
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="name"
                type="text"
                required
                placeholder="Full Name"
                onChange={handleChange}
                value={formData.name}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaUser className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Sex
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 z-10 flex items-center pl-3 pointer-events-none">
                <FaVenusMars className="w-5 h-5 text-gray-400" />
              </div>
              <Select
                name="sex"
                onChange={dropDownChange}
                options={genderOptions}
                className="w-full"
                placeholder={placeHolderText("Select Gender")}
                styles={{
                  ...style,
                  control: (provided) => ({
                    ...provided,
                    paddingLeft: "2.5rem",
                  }),
                }}
                value={formData.sex}
              />
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Parents Name
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="parents_name"
                type="text"
                required
                placeholder="Parents' Names"
                onChange={handleChange}
                value={formData.parents_name}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaUsers className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Date of Birth
            </label>
            <DatePicker
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              name="date_of_birth"
              onChange={(date, dateString) =>
                setFormData({ ...formData, date_of_birth: dateString })
              }
              disabledDate={(current) =>
                current && current > moment().endOf("day")
              }
              format="MM-DD-YYYY"
              suffixIcon={<FaBirthdayCake className="text-gray-400" />}
              value={moment(formData.date_of_birth, "YYYY-MM-DD")}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Baptism Date
            </label>
            <DatePicker
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              name="date_of_baptism"
              onChange={(date, dateString) =>
                setFormData({ ...formData, date_of_baptism: dateString })
              }
              disabledDate={(current) =>
                current && current > moment().endOf("day")
              }
              format="MM-DD-YYYY"
              suffixIcon={<FaCalendarAlt className="text-gray-400" />}
              value={moment(formData.date_of_baptism, "YYYY-MM-DD")}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Place of Confirmation
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="place_of_confirmation"
                type="text"
                required
                placeholder="Place of Confirmation"
                onChange={handleChange}
                value={formData.place_of_confirmation}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaChurch className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Confirmed By
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="confirmed_by"
                type="text"
                required
                placeholder="Confirmed by"
                onChange={handleChange}
                value={formData.confirmed_by}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaCross className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 mt-6 md:grid-cols-2">
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Place of Residence
          </label>
          <div className="relative">
            <input
              className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              name="place_of_residence"
              type="text"
              required
              placeholder="Place of Residence"
              onChange={handleChange}
              value={formData.place_of_residence}
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FaMapMarkerAlt className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Presbyter
          </label>
          <div className="relative">
            <input
              className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              name="presbyter"
              type="text"
              required
              placeholder="Presbyter"
              onChange={handleChange}
              value={formData.presbyter}
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FaChurch className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-8 space-x-4">
        <button
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={handleConfirmationRegister}
        >
          Update Record
        </button>
      </div>
    </div>
  );
};

export default UpdateConfirmationChild;
